/*  
  Developers: Sakshi Bhise  
  File: App.tsx  
  Date: 06/18/2025  
  Project Name: AskHR  
  Team: DSET  
  
  Purpose:  
  Entry point for the AskHR React application.  
  Mounts the root App component, initializes theme and Fluent UI icons,  
  provides application-wide state/context, configures i18n (internationalization),  
  routing, layout and accessibility adjustments, and language-aware theming.  
*/

import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Stack } from '@fluentui/react';
import { AppStateContext } from './state/AppProvider';
import { AuthContext } from './state/AuthContext';
import { ChatHistoryLoadingState } from './api/models';
import styles from './pages/chat/Chat.module.css';
import SidebarLayout from './components/Sidebar/SidebarLayout';
import NoPage from './pages/NoPage';
import Chat from './pages/chat/Chat';
import SigninHandler from './pages/SignInHandler';
import AuthGate from './state/AuthGate';
import { logEvent } from './api';
import { TevaThemeProvider, Loader } from '@teva/ui';
import { ThemeProvider as AppThemeProvider, useAppTheme } from './state/ThemeContext';

// Tracks the visible viewport height as a CSS variable so the layout shrinks
// correctly when the iOS soft keyboard pushes the viewport up. Without this,
// height:100% and 100dvh remain pinned to the full screen height on iOS Safari,
// causing the input to slide behind the keyboard.
function useIOSViewportHeight() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      document.documentElement.style.setProperty('--app-height', `${vv.height}px`);
      // iOS Safari scrolls window.scrollY when the keyboard opens to try to
      // reveal the focused input. Because #root is position:fixed this scroll
      // offset does nothing useful but can cause sub-pixel jitter — reset it.
      if (window.scrollY !== 0) window.scrollTo(0, 0);
    };

    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);
}

const ThemedApp: React.FC = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';
  const { resolved } = useAppTheme();

  useIOSViewportHeight();

  return (
    <TevaThemeProvider theme={'light'} applyTo={'element'} isRTL={isRTL}>
      <div style={{ height: '100%' }}>
        <BrowserRouter>
          <Routes>
            <Route path="/signin" element={<SigninHandler />} />
            <Route
              path="/"
              element={
                <AuthGate>

                  <Gatekeeper>
                    <SidebarLayout />
                  </Gatekeeper>

                </AuthGate>
              }
            >
              <Route index element={<Chat />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </div>
    </TevaThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <AppThemeProvider>
      <ThemedApp />
    </AppThemeProvider>
  );
};

const spinnerStyles = {
  label: {
    fontSize: '16px',
  },
  circle: {
    width: 100,
    height: 100,
    borderWidth: 6,
  },
};

const Gatekeeper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const appStateContext = useContext(AppStateContext);
  const [ready, setReady] = useState(false);
  const [denyReason, setDenyReason] = useState<null | 'device' | 'country' | 'group' | 'not_found'>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState<boolean>(false);
  const { token }: any = useContext(AuthContext);
  useEffect(() => {
    if (!appStateContext?.state.loggedInUserCountry || !appStateContext.state.userEmployeeGroup) {
      return;
    }
    const userCountry = appStateContext.state.loggedInUserCountry.toLowerCase();
    const group = appStateContext.state.userEmployeeGroup.toLowerCase();
    const userGId = appStateContext?.state?.userGId;

    if (group === 'user_not_found') {
      logEvent({ "eventType": "Access denied: User not found in employee_permissions" }, userCountry, userGId, token, "ERROR");
      setDenyReason('not_found');
    } else if (group === 'contingent worker') {
      logEvent({ "eventType": "Access denied: User identified as contingent worker, not a Teva employee" }, userCountry, userGId, token, "ERROR");
      setDenyReason('group');
    }
    else {
      setDenyReason(null);
    }
    setReady(true);
  }, [appStateContext?.state.loggedInUserCountry, appStateContext?.state.impUserCountry, appStateContext?.state.userEmployeeGroup]);

  useEffect(() => {
    setIsHistoryLoading(appStateContext?.state.chatHistoryLoadingState === ChatHistoryLoadingState.Loading);
  }, [appStateContext?.state.chatHistoryLoadingState]);

  if (
    !appStateContext?.state.loggedInUserCountry ||
    !appStateContext?.state.userEmployeeGroup ||
    !ready ||
    isHistoryLoading
  ) {
    return (
      <div className={styles.container}>
        <Stack className={styles.chatEmptyState}>
          <Loader isLoading={true} loaderText='Logging you in...' imgWidth={400} isFullScreen={true} isOverlay={true} />
        </Stack>
      </div>
    );
  }

  if (denyReason) {
    // Pass the reason to NoPage so it can display the correct message  
    return <NoPage reason={denyReason} variant={'desktop'} />;
  }

  return <>{children}</>;
};


export default App;  